import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = ({
  selected,
  onChange,
  dateFormat = 'MMM d, yyyy',
  placeholderText = 'Select date',
  className = '',
  calendarClassName = '',
  popperClassName = '',
  showPopperArrow = true,
  filterDate,
  minDate,
  maxDate,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  onFocus,
  onBlur,
  id,
  name,
  tabIndex,
  ...otherProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (selected instanceof Date) {
      return selected;
    }
    if (selected && typeof selected === 'string') {
      return new Date(selected);
    }
    return new Date();
  });
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef(null);
  const calendarRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768;
    };
    
    setIsMobile(checkMobile());
    
    const handleResize = () => setIsMobile(checkMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Format date according to dateFormat prop
  const formatDate = (date) => {
    if (!date) return '';
    if (typeof dateFormat === 'string') {
      date = new Date(date);
      if (isNaN(date)) return '';
    }
    const options = {};
    if (dateFormat.includes('MMM')) {
      options.month = 'short';
    } else if (dateFormat.includes('MM')) {
      options.month = '2-digit';
    } else if (dateFormat.includes('M')) {
      options.month = 'numeric';
    }
    
    if (dateFormat.includes('d')) {
      options.day = 'numeric';
    } else if (dateFormat.includes('dd')) {
      options.day = '2-digit';
    }
    
    if (dateFormat.includes('yyyy')) {
      options.year = 'numeric';
    } else if (dateFormat.includes('yy')) {
      options.year = '2-digit';
    }

    return date.toLocaleDateString('en-US', options);
  };

  // Convert date to YYYY-MM-DD format for native input
  const formatDateForNative = (date) => {
    if (!date) return '';
    const validDate = new Date(date);
    if (isNaN(validDate)) return '';
    return validDate.toISOString().split('T')[0];
  };

  // Handle native date input change
  const handleNativeDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const date = new Date(value + 'T00:00:00');
      onChange(date);
    } else {
      onChange(null);
    }
    if (onBlur) onBlur(e);
  };

  // Check if date should be disabled
  const isDateDisabled = (date) => {
    if (!date) return true;
    
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    if (filterDate && !filterDate(date)) return true;
    
    return false;
  };

  // Handle date selection
  const handleDateSelect = (day) => {
    if (!day) return;
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const selectedDate = new Date(year, month, day);
    
    if (!isDateDisabled(selectedDate)) {
      onChange(selectedDate);
      setIsOpen(false);
    }
  };

  // Handle input click
  const handleInputClick = () => {
    if (!disabled && !readOnly) {
      setIsOpen(true); // Always open on click, don't toggle
    }
  };

  // Handle input focus
  const handleInputFocus = (e) => {
    if (!disabled && !readOnly) {
      setIsOpen(true);
    }
    if (onFocus) onFocus(e);
  };

  // Handle input blur
  const handleInputBlur = (e) => {
    // Delay blur to allow calendar clicks
    setTimeout(() => {
      if (calendarRef.current && !calendarRef.current.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 150);
    if (onBlur) onBlur(e);
  };

  // Navigation functions
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const validCurrentMonth = currentMonth instanceof Date ? currentMonth : new Date();

    const year = validCurrentMonth.getFullYear();
    const month = validCurrentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  // Check if date is selected
  const isDateSelected = (day) => {
    if (!day || !selected) return false;
    
    if (!(selected instanceof Date)) {
      selected = new Date(selected);
      if (isNaN(selected)) return false;
    }
    
    const validCurrentMonth = currentMonth instanceof Date ? currentMonth : new Date();

    const year = validCurrentMonth.getFullYear();
    const month = validCurrentMonth.getMonth();
    const date = new Date(year, month, day);
    return date.toDateString() === selected.toDateString();
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        calendarRef.current && 
        !calendarRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  // If mobile, render native date input
  if (isMobile) {
    return (
      <div className="relative inline-block w-full">
        <input
          ref={inputRef}
          type="date"
          value={formatDateForNative(selected)}
          onChange={handleNativeDateChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          id={id}
          name={name}
          tabIndex={tabIndex}
          min={minDate ? formatDateForNative(minDate) : undefined}
          max={maxDate ? formatDateForNative(maxDate) : undefined}
          className={`
            w-full px-3 py-2 border border-gray-300 rounded-md
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            ${className}
          `}
          {...otherProps}
        />
      </div>
    );
  }

  // Desktop custom date picker
  const days = generateCalendarDays();

  return (
    <div className="relative inline-block w-full">
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={formatDate(selected)}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText}
          readOnly
          disabled={disabled}
          autoFocus={autoFocus}
          id={id}
          name={name}
          tabIndex={tabIndex}
          className={`
            w-full px-3 py-2 pr-10 border border-gray-300 rounded-md
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            cursor-pointer
            ${className}
          `}
          {...otherProps}
        />
        {/* <Calendar className={`absolute right-3 top-1/2 transform -translate-y-1/2 
          h-5 w-5 text-gray-400 pointer-events-none`} /> */}
      </div>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div
          ref={calendarRef}
          className={`
            absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50
            w-80 p-4
            ${popperClassName}
          `}
          style={{
            // Ensure calendar doesn't go off screen
            maxWidth: '100vw',
            left: '0',
            right: 'auto'
          }}
        >
          {/* Popper Arrow */}
          {showPopperArrow && (
            <div className={`absolute -top-1 left-6 w-2 h-2 bg-white border-l 
              border-t border-gray-200 transform rotate-45`}></div>
          )}

          <div className={`${calendarClassName}`}>
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={prevMonth}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                tabIndex={-1}
              >
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>
              
              <h4 className="text-lg font-semibold text-gray-900">
                {currentMonth.toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </h4>
              
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                tabIndex={-1}
              >
                <ChevronRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                const date = day ? new Date(year, month, day) : null;
                const disabled = day ? isDateDisabled(date) : true;
                const selected = day ? isDateSelected(day) : false;
                const isToday = date && date.toDateString() === new Date().toDateString();

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDateSelect(day)}
                    disabled={disabled}
                    tabIndex={day ? 0 : -1}
                    className={`
                      h-10 w-10 text-sm rounded-md transition-all duration-150
                      focus:outline-none focus:ring-2 focus:ring-blue-500
                      ${!day ? 'invisible' : ''}
                      ${disabled 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-700 hover:bg-blue-50 active:bg-blue-100'
                      }
                      ${selected 
                        ? 'bg-blue-500 text-white hover:bg-blue-600' 
                        : ''
                      }
                      ${isToday && !selected 
                        ? 'bg-blue-100 text-blue-600 font-semibold' 
                        : ''
                      }
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  /** Currently selected date */
  selected: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),

  /** Function called when date is selected */
  onChange: PropTypes.func.isRequired,
  
  /** Date format string (e.g., "MMM d, yyyy") */
  dateFormat: PropTypes.string,
  
  /** Placeholder text for input */
  placeholderText: PropTypes.string,
  
  /** CSS class for input element */
  className: PropTypes.string,
  
  /** CSS class for calendar container */
  calendarClassName: PropTypes.string,
  
  /** CSS class for popper container */
  popperClassName: PropTypes.string,
  
  /** Whether to show arrow on calendar popup */
  showPopperArrow: PropTypes.bool,
  
  /** Function to filter available dates */
  filterDate: PropTypes.func,
  
  /** Minimum selectable date */
  minDate: PropTypes.instanceOf(Date),
  
  /** Maximum selectable date */
  maxDate: PropTypes.instanceOf(Date),
  
  /** Whether input is disabled */
  disabled: PropTypes.bool,
  
  /** Whether input is read-only */
  readOnly: PropTypes.bool,
  
  /** Whether input should auto-focus */
  autoFocus: PropTypes.bool,
  
  /** Function called on input focus */
  onFocus: PropTypes.func,
  
  /** Function called on input blur */
  onBlur: PropTypes.func,
  
  /** Input element ID */
  id: PropTypes.string,
  
  /** Input element name */
  name: PropTypes.string,
  
  /** Input element tab index */
  tabIndex: PropTypes.number
};

export default DatePicker;